import type {IntlShape} from 'react-intl';

/**
 * The company timesheet lock as the data importer sees it. The importer creates time entries through
 * an endpoint that skips the approval-submission lock but still enforces the company lock, so a row
 * dated on or before `lockedDate` is refused by the server unless its person is excluded from the
 * company lock. The refusal happens after upload, where nobody sees it, so it has to be caught here.
 */
export type TimesheetLock = {
	/** `YYYY-MM-DD`; entries dated on or before it are refused. */
	lockedDate: string;
	/** Lowercased emails of persons excluded from the company lock. */
	exemptEmails: string[];
} | null;

type LockedPeriod = {
	year: number | null | undefined;
	month: number | null | undefined;
	day: number | null | undefined;
};

type LockPerson = {
	email: string | null | undefined;
	excludeFromCompanyLockedPeriod: boolean | null | undefined;
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_FIRST_DATE = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/;

const pad = (n: number): string => String(n).padStart(2, '0');

/**
 * The `YYYY-MM-DD` the importer will upload for `value`, or null when that cannot be known here.
 *
 * The importer converts most typed dates before an edit reaches the row hooks, but a day-first date
 * whose day is above 12 (`15/09/2026`, `15.09.2026`) arrives as typed, because it cannot be read
 * month-first; the importer then uploads it day-first. That is the only other form read here: a
 * day-first date with a day of 12 or less is read month-first by the importer, so it never arrives
 * unconverted, and guessing at it could disagree with what gets uploaded.
 */
const toUploadedIsoDate = (value: string): string | null => {
	if (ISO_DATE.test(value)) {
		return value;
	}
	const match = DAY_FIRST_DATE.exec(value);
	if (!match) {
		return null;
	}
	const [day, month, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
	const date = new Date(Date.UTC(year, month - 1, day));
	const exists = date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
	return day > 12 && exists ? `${year}-${pad(month)}-${pad(day)}` : null;
};

export const buildTimesheetLock = (
	hasTimeLockingModule: boolean,
	{year, month, day}: LockedPeriod,
	persons: readonly LockPerson[]
): TimesheetLock => {
	if (!hasTimeLockingModule || year == null || month == null || day == null) {
		return null;
	}
	return {
		lockedDate: `${year}-${pad(month)}-${pad(day)}`,
		exemptEmails: persons
			.filter(p => p.excludeFromCompanyLockedPeriod && p.email)
			.map(p => (p.email as string).trim().toLowerCase()),
	};
};

/**
 * Whether the server will refuse a time entry for `email` on `date`. A date whose uploaded value
 * cannot be known (see `toUploadedIsoDate`) returns false: the importer cannot tell where it falls,
 * so it claims nothing about it.
 *
 * An email that matches no existing person is treated as locked, since the importer creates that
 * person and a new person is never excluded from the lock.
 */
export const isTimeRegInLockedPeriod = (
	lock: TimesheetLock,
	date: unknown,
	email: unknown,
	companyId: number | string | undefined
): boolean => {
	const isoDate = lock && typeof date === 'string' ? toUploadedIsoDate(date.trim()) : null;
	if (!lock || isoDate === null || isoDate > lock.lockedDate) {
		return false;
	}
	const normalizedEmail = typeof email === 'string' ? email.trim().toLowerCase() : '';
	const isExempt =
		normalizedEmail !== '' &&
		(lock.exemptEmails.includes(normalizedEmail) || lock.exemptEmails.includes(`${companyId}_${normalizedEmail}`));
	return !isExempt;
};

/** The lock date as the user reads it, e.g. "Sep 30, 2026" in English. */
export const formatLockedDate = (intl: IntlShape, lockedDate: string): string => {
	const [year, month, day] = lockedDate.split('-').map(Number);
	return intl.formatDate(new Date(year, month - 1, day), {year: 'numeric', month: 'short', day: 'numeric'});
};
